/**
 * Utilitários do elemento Quiz de reforço (Microquiz).
 * Compartilhado entre builder, preview e exportação HTML.
 */

export const MICROQUIZ_MAX_QUESTIONS = 4;

/** Nota mínima (%) para liberar gabarito quando showFeedbackOnPass está ativo. */
export function getMicroquizPassingScore(manifest) {
  const n = parseInt(manifest?.passingScorePercent, 10);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 70;
}

/**
 * Define se o gabarito detalhado (certo/errado + explicação) pode ser exibido.
 * @param {object} manifest — content do widget ou entrada do quizManifest
 * @param {number} attemptNum — número da tentativa atual (1-based)
 * @param {number|null|undefined} scorePercent — nota da tentativa (quando já finalizada)
 */
export function shouldShowMicroquizDetailedFeedback(manifest, attemptNum, scorePercent) {
  const policy = manifest?.attemptPolicy || 'best_unlimited';
  const passing = getMicroquizPassingScore(manifest);
  const showOnPass = manifest?.showFeedbackOnPass !== false;

  if (showOnPass && typeof scorePercent === 'number' && scorePercent >= passing) {
    return true;
  }

  const attempt = Math.max(1, parseInt(attemptNum, 10) || 1);

  if (policy === 'best_limited') {
    const max = Math.max(1, parseInt(manifest?.maxAttempts, 10) || 3);
    return attempt >= max;
  }
  if (policy === 'single') {
    return attempt >= 1;
  }

  const raw = parseInt(manifest?.showFeedbackFromAttempt, 10);
  if (!Number.isFinite(raw) || raw <= 0) {
    return false;
  }
  return attempt >= raw;
}

/** Monta objeto de política a partir do content do widget (builder/preview). */
export function buildMicroquizFeedbackManifest(content) {
  const c = content || {};
  return {
    attemptPolicy: c.attemptPolicy || 'best_unlimited',
    maxAttempts: c.maxAttempts,
    showFeedbackFromAttempt: c.showFeedbackFromAttempt,
    passingScorePercent: c.passingScorePercent,
    showFeedbackOnPass: c.showFeedbackOnPass,
    graded: !!c.graded,
  };
}

const ELEMENTS_WITH_EMBEDDED_CHECKPOINT = new Set([
  'video',
  'panda-video',
  'text',
  'image',
  'h5p',
  'styled-card',
  'section-header',
  'title-basic',
  'blockquote',
  'alert',
  'asaas-payment',
  'mercadopago-payment',
]);

function escHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Percorre colunas/linhas aninhadas e visita cada widget. */
export function walkLayoutWidgets(rows, visitor) {
  if (!Array.isArray(rows)) return;
  const visitCol = (col) => {
    if (!col) return;
    for (const w of col.widgets || []) {
      visitor(w);
    }
    for (const nr of col.nestedRows || []) {
      walkLayoutWidgets([nr], visitor);
    }
  };
  for (const row of rows) {
    for (const col of row.columns || []) {
      visitCol(col);
    }
  }
}

/** Hash estável do conteúdo avaliativo (perguntas + gabarito). */
export function hashMicroquizQuestions(questions) {
  const payload = JSON.stringify(
    (questions || []).map((q) => ({
      id: q.id,
      type: q.type || 'mcq',
      prompt: q.prompt || '',
      options: q.type === 'truefalse' ? ['true', 'false'] : (q.options || []),
      correctIndex: q.correctIndex ?? 0,
    }))
  );
  let h = 5381;
  for (let i = 0; i < payload.length; i++) {
    h = ((h << 5) + h) + payload.charCodeAt(i);
    h |= 0;
  }
  return (h >>> 0).toString(36);
}

export function createDefaultMicroquizQuestion(t, index = 1) {
  const n = index;
  return {
    id: `q${n}`,
    type: 'mcq',
    prompt: t
      ? t('microquizDefaultQuestion', { n, defaultValue: `Pergunta ${n}` })
      : `Pergunta ${n}`,
    options: [
      t ? t('microquizDefaultOptionA', { defaultValue: 'Opção A' }) : 'Opção A',
      t ? t('microquizDefaultOptionB', { defaultValue: 'Opção B' }) : 'Opção B',
      t ? t('microquizDefaultOptionC', { defaultValue: 'Opção C' }) : 'Opção C',
    ],
    correctIndex: 0,
    explanation: '',
  };
}

export function createDefaultMicroquizContent(t) {
  return {
    triggerLabel: t
      ? t('microquizDefaultTrigger', { defaultValue: 'Teste seu conhecimento' })
      : 'Teste seu conhecimento',
    btnStyle: 'button',
    btnBg: '#3b82f6',
    btnText: '#ffffff',
    modalTitle: t
      ? t('microquizDefaultModalTitle', { defaultValue: 'Quiz de reforço' })
      : 'Quiz de reforço',
    align: 'left',
    modalPanelWidthPx: 560,
    modalPanelMaxHeightPx: 520,
    graded: false,
    weight: 0,
    attemptPolicy: 'best_unlimited',
    maxAttempts: 3,
    showFeedbackFromAttempt: 0,
    passingScorePercent: 70,
    showFeedbackOnPass: true,
    requiresCheckpointId: null,
    requiresCheckpointLabel: null,
    questions: [createDefaultMicroquizQuestion(t, 1), createDefaultMicroquizQuestion(t, 2)],
  };
}

function stripHtmlToPlainText(html) {
  return String(html ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Nome legível do elemento para identificar checkpoints no builder e no Moodle. */
export function resolveCheckpointDisplayLabel(widget, t, index = 0) {
  if (!widget) return '';
  const custom = (widget.type === 'checkpoint'
    ? widget.content?.label
    : widget.content?.checkpoint?.label);
  if (String(custom || '').trim()) {
    return String(custom).trim().slice(0, 80);
  }

  const c = widget.content || {};
  const typeFallback = {
    video: t ? t('builder:elVideo', { defaultValue: 'Vídeo' }) : 'Vídeo',
    'panda-video': t ? t('builder:elPandaVideo', { defaultValue: 'Vídeo Panda' }) : 'Vídeo Panda',
    text: t ? t('builder:elText', { defaultValue: 'Texto' }) : 'Texto',
    image: t ? t('builder:elImage', { defaultValue: 'Imagem' }) : 'Imagem',
    h5p: t ? t('builder:elH5P', { defaultValue: 'H5P' }) : 'H5P',
    'styled-card': t ? t('builder:elStyledCard', { defaultValue: 'Card estilizado' }) : 'Card estilizado',
    'section-header': t ? t('builder:elSectionHeader', { defaultValue: 'Cabeçalho de seção' }) : 'Cabeçalho de seção',
    'title-basic': t ? t('builder:elTitleBasic', { defaultValue: 'Título âncora' }) : 'Título âncora',
    blockquote: t ? t('builder:elBlockquote', { defaultValue: 'Citação' }) : 'Citação',
    alert: t ? t('builder:elAlert', { defaultValue: 'Alerta' }) : 'Alerta',
    'asaas-payment': t ? t('builder:elAsaasPayment', { defaultValue: 'Pagamento Asaas' }) : 'Pagamento Asaas',
    'mercadopago-payment': t ? t('builder:elMercadoPagoPayment', { defaultValue: 'Pagamento Mercado Pago' }) : 'Pagamento Mercado Pago',
    checkpoint: t ? t('builder:elCheckpoint', { defaultValue: 'Checkpoint' }) : 'Checkpoint',
  };

  const titleCandidates = [
    c.title,
    c.cardTitle,
    c.triggerLabel,
    c.modalTitle,
    c.activityTitle,
    stripHtmlToPlainText(c.html),
    stripHtmlToPlainText(c.text),
  ].map((s) => String(s || '').trim()).filter(Boolean);

  if (titleCandidates.length > 0) {
    return titleCandidates[0].slice(0, 80);
  }

  const base = typeFallback[widget.type] || widget.type;
  const n = index + 1;
  return t
    ? t('builder:checkpointDefaultName', { name: base, n, defaultValue: `${base} ${n}` })
    : `${base} ${n}`;
}

function formatCheckpointOptionSuffix(widget, t) {
  if (widget.type === 'checkpoint') {
    const sec = Math.round((parseInt(widget.content?.durationMs, 10) || 5000) / 1000);
    return t
      ? t('builder:microquizCheckpointOptionTime', { sec, defaultValue: `${sec}s` })
      : `${sec}s`;
  }
  const ck = widget.content?.checkpoint || {};
  const mode = ck.completionMode || 'time';
  if (mode === 'video') {
    const pct = ck.videoPercent ?? 75;
    return t
      ? t('builder:microquizCheckpointOptionVideo', { pct, defaultValue: `vídeo ${pct}%` })
      : `vídeo ${pct}%`;
  }
  const sec = Math.round((parseInt(ck.durationMs, 10) || 5000) / 1000);
  return t
    ? t('builder:microquizCheckpointOptionTime', { sec, defaultValue: `${sec}s` })
    : `${sec}s`;
}

/** IDs dos widgets-checkpoint na ordem do layout (mesma ordem de ckIds no export). */
export function enumerateLayoutCheckpointIds(rows) {
  const ids = [];
  walkLayoutWidgets(rows, (w) => {
    if (w.type === 'checkpoint') {
      ids.push(w.id);
      return;
    }
    const ck = w.content?.checkpoint;
    if (ck?.enabled && ELEMENTS_WITH_EMBEDDED_CHECKPOINT.has(w.type)) {
      ids.push(w.id);
    }
  });
  return ids;
}

/** Checkpoints disponíveis para vínculo (dropdown na gaveta). */
export function collectCheckpointOptionsFromLayout(rows, t) {
  const out = [];
  enumerateLayoutCheckpointIds(rows).forEach((id, ckIndex) => {
    let widget = null;
    walkLayoutWidgets(rows, (w) => {
      if (w.id === id) widget = w;
    });
    if (!widget) return;
    const name = resolveCheckpointDisplayLabel(widget, t, ckIndex);
    const suffix = formatCheckpointOptionSuffix(widget, t);
    out.push({ id, label: `${name} · ${suffix}`, checkpointIndex: ckIndex });
  });
  return out;
}

/** Resumo para o modal de exportação e indicadores no builder. */
export function summarizeMicroquizzesInLayout(rows) {
  let total = 0;
  let gradedCount = 0;
  let weightSum = 0;
  walkLayoutWidgets(rows, (w) => {
    if (w.type !== 'reinforcement-quiz') return;
    total += 1;
    const c = w.content || {};
    if (c.graded && (parseInt(c.weight, 10) || 0) > 0) {
      gradedCount += 1;
      weightSum += Math.min(100, Math.max(0, parseInt(c.weight, 10) || 0));
    }
  });
  return {
    total,
    gradedCount,
    weightSum,
    weightsOk: gradedCount === 0 || weightSum === 100,
    weightRemaining: Math.max(0, 100 - weightSum),
  };
}

/**
 * HTML exportado do widget + entrada no quizManifest.
 * @param {object} widget
 * @param {object[]} quizList — mutável; recebe metadados do manifest
 * @param {object} [labels] — strings i18n para V/F no export
 */
export function buildMicroquizExportInnerHtml(widget, quizList, labels = {}, options = {}) {
  const c = widget.content || {};
  const questions = (c.questions || []).slice(0, MICROQUIZ_MAX_QUESTIONS);
  const hash = hashMicroquizQuestions(questions);
  const weight = Math.min(100, Math.max(0, parseInt(c.weight, 10) || 0));
  const graded = !!c.graded && weight > 0 && questions.length > 0;
  const trueLabel = labels.trueLabel || 'Verdadeiro';
  const falseLabel = labels.falseLabel || 'Falso';

  let requiresCheckpointId = c.requiresCheckpointId || null;
  let requiresCheckpointLabel = c.requiresCheckpointLabel || null;
  let requiresCheckpointIndex = -1;
  if (options.checkpointIds && requiresCheckpointId) {
    const idx = options.checkpointIds.indexOf(requiresCheckpointId);
    if (idx >= 0) requiresCheckpointIndex = idx;
  }
  if (requiresCheckpointIndex < 0 && options.rows) {
    const opts = collectCheckpointOptionsFromLayout(options.rows, options.t || null);
    if (requiresCheckpointId) {
      const match = opts.find((o) => o.id === requiresCheckpointId);
      if (match) {
        if (!requiresCheckpointLabel) {
          requiresCheckpointLabel = String(match.label).split(' · ')[0].trim() || match.label;
        }
        if (typeof match.checkpointIndex === 'number') {
          requiresCheckpointIndex = match.checkpointIndex;
        }
      }
    }
  }

  quizList.push({
    id: widget.id,
    hash,
    graded,
    weight: graded ? weight : 0,
    attemptPolicy: c.attemptPolicy || 'best_unlimited',
    maxAttempts: Math.max(1, parseInt(c.maxAttempts, 10) || 3),
    showFeedbackFromAttempt: Math.max(0, parseInt(c.showFeedbackFromAttempt, 10) || 0),
    passingScorePercent: getMicroquizPassingScore(c),
    showFeedbackOnPass: c.showFeedbackOnPass !== false,
    requiresCheckpointId: requiresCheckpointId || null,
    requiresCheckpointLabel: requiresCheckpointLabel || null,
    requiresCheckpointIndex: requiresCheckpointIndex >= 0 ? requiresCheckpointIndex : null,
    questionCount: questions.length,
  });

  const modalId = `geppetto_mq_modal_${widget.id}`;
  const btnBg = c.btnBg || '#3b82f6';
  const btnText = c.btnText || '#ffffff';
  const bStyle = c.btnStyle || 'button';
  const trigStyle = bStyle === 'outline'
    ? `background:transparent;color:${btnBg};border:2px solid ${btnBg}`
    : bStyle === 'link'
      ? `background:transparent;color:${btnBg};padding:0;text-decoration:underline;border:none`
      : `background:${btnBg};color:${btnText};border:none`;
  const panelW = Math.min(720, Math.max(320, parseInt(c.modalPanelWidthPx, 10) || 560));
  const panelMaxH = Math.min(720, Math.max(240, parseInt(c.modalPanelMaxHeightPx, 10) || 520));
  const triggerLabel = escHtml(c.triggerLabel || 'Teste seu conhecimento');
  const modalTitle = escHtml(c.modalTitle || 'Quiz de reforço');
  const requiresCk = requiresCheckpointId ? escHtml(requiresCheckpointId) : '';
  const requiresCkLabel = requiresCheckpointLabel ? escHtml(requiresCheckpointLabel) : '';
  const requiresCkIndexAttr = requiresCheckpointIndex >= 0 ? String(requiresCheckpointIndex) : '';

  const defPayload = {
    widgetId: widget.id,
    questions: questions.map((q) => {
      const type = q.type === 'truefalse' ? 'truefalse' : 'mcq';
      const options = type === 'truefalse'
        ? [trueLabel, falseLabel]
        : (q.options || []).map((o) => String(o ?? ''));
      return {
        id: q.id || `q_${Math.random().toString(36).slice(2, 7)}`,
        type,
        prompt: String(q.prompt || ''),
        options,
        correctIndex: Math.max(0, parseInt(q.correctIndex, 10) || 0),
        explanation: String(q.explanation || ''),
      };
    }),
  };

  const defJson = JSON.stringify(defPayload).replace(/</g, '\\u003c');

  return `<div class="geppetto-microquiz-wrap" data-quiz-id="${escHtml(widget.id)}" data-requires-ck="${requiresCk}" data-requires-ck-label="${requiresCkLabel}" data-requires-ck-index="${requiresCkIndexAttr}" data-gep-cmid="[[GEPPETTO_CMID]]" data-gep-sesskey="[[GEPPETTO_SESSKEY]]" style="text-align:${c.align || 'left'};width:100%;box-sizing:border-box">`
    + `<button type="button" class="geppetto-microquiz-trigger" data-modal-id="${modalId}" data-quiz-id="${escHtml(widget.id)}" style="display:inline-flex;align-items:center;gap:8px;padding:10px 22px;border-radius:6px;cursor:pointer;font-weight:600;font-size:14px;${trigStyle}">${triggerLabel}</button>`
    + `<div id="${modalId}" class="geppetto-microquiz-modal" data-quiz-id="${escHtml(widget.id)}" role="dialog" aria-modal="true" aria-labelledby="${modalId}_title" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:100001;align-items:center;justify-content:center;padding:1rem;box-sizing:border-box">`
    + `<div class="geppetto-microquiz-panel" style="background:#fff;border-radius:12px;padding:1.5rem;width:min(${panelW}px,calc(100vw - 32px));max-width:100%;max-height:min(${panelMaxH}px,90vh);box-shadow:0 25px 60px rgba(0,0,0,.3);position:relative;display:flex;flex-direction:column;overflow:hidden;box-sizing:border-box">`
    + `<button type="button" class="geppetto-microquiz-close" aria-label="Fechar" style="position:absolute;top:12px;right:14px;background:none;border:none;font-size:22px;cursor:pointer;color:#94a3b8;z-index:1">✕</button>`
    + `<h2 id="${modalId}_title" style="margin:0 0 1rem;padding-right:36px;color:#1e293b;font-size:1.25rem;flex-shrink:0">${modalTitle}</h2>`
    + `<div class="geppetto-microquiz-body" style="flex:1;min-height:0;overflow-y:auto"></div>`
    + `</div></div>`
    + `<script type="application/json" class="geppetto-microquiz-def" data-quiz-id="${escHtml(widget.id)}">${defJson}</script>`
    + `</div>`;
}
